/**
 * App shell — behaviour for the top bar and sidebar on every signed-in page.
 *
 * 1. Area links in the top bar never wrap: the ones that do not fit collapse
 *    into a "More" rf-dropdown at the end of the row. Re-measured on resize,
 *    when fonts finish loading, and after the chrome is refreshed.
 *    The hub tab strip (#shell-subnav) scrolls sideways when it doesn't fit;
 *    its active tab is scrolled into view at the same moments.
 * 2. On htmx navigation into #page-content, the response is a full page:
 *    replace `#shell-sidebar`, `#shell-areas` and `#shell-subnav` from it so
 *    the current area, object and hub tabs follow the page. Only for page swaps, never for the partial
 *    requests (filters, sorts, tabs) that target something else.
 *
 * 3. Record pages add a right rail (#shell-rail) and its panel (#shell-panel).
 *    A rail button opens its panel section; pressing it again closes the
 *    panel. Pinned, the panel is a grid column from 1200px up and opens on
 *    load at its data-panel-default section; unpinned or narrower, it floats
 *    over the page and a click outside it closes it. The pin choice is kept in localStorage.
 *    On htmx page navigation the rail and panel are added, replaced or
 *    removed from the response along with the sidebar.
 *    A section the user opens is written to the URL as #rail-<section> and
 *    cleared when the panel closes, so the link reopens it; on load that hash
 *    wins over data-panel-default, and one this page has no section for is
 *    dropped. Tab clicks on the record keep it: htmx tabs swap only a pane
 *    inside the page, and a plain-link tab (the rental page's) reloads with
 *    the hash carried over. Navigating to another page drops it. sheets.js
 *    keeps the hash when it writes ?sheet=.
 *
 * Templates: includes/_shell-topbar.html, includes/_shell-sidebar.html,
 * acqs/_lead_shell_rail.html.
 */
(function () {
    var timer;
    var moduleRequest;
    var MENU_ID = 'shell-more-menu';

    function nav() {
        return document.querySelector('.shell__top .header-nav');
    }

    // The menu is a popout: rf-dropdowns.js moves it to <body> while open, so
    // it is found by id, never by position inside the overflow container.
    function menuEl() {
        return document.getElementById(MENU_ID);
    }

    function navItems(navEl) {
        return Array.prototype.slice.call(navEl.querySelectorAll(
            ':scope > .header-navlink, :scope > [data-shell-topnav-dropdown]'
        ));
    }

    function navItemLink(item) {
        return item.matches('a.header-navlink') ? item : item.querySelector('.header-navlink');
    }

    function closeNavPopout(navEl, selector) {
        var menu = document.querySelector(selector);
        if (!menu || !menu.classList.contains('show')) return;
        var trigger = navEl.querySelector('[aria-controls="' + menu.id + '"]');
        if (trigger) trigger.click();
    }

    function closeNavPopouts(navEl) {
        closeNavPopout(navEl, '#' + MENU_ID);
        document.querySelectorAll('.rf-dropdown-menu[id^="shell-"]').forEach(function (menu) {
            closeNavPopout(navEl, '#' + menu.id);
        });
    }

    function ensureOverflow(navEl) {
        var overflow = navEl.querySelector('.header-nav-overflow');
        if (overflow) return overflow;
        var stale = menuEl();
        if (stale) stale.parentNode.removeChild(stale);   // left behind by a chrome refresh
        overflow = document.createElement('div');
        overflow.className = 'rf-dropdown header-nav-overflow';
        overflow.hidden = true;
        var trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'header-navlink';
        trigger.setAttribute('data-rf-dropdown', '');
        trigger.setAttribute('aria-controls', MENU_ID);
        trigger.innerHTML = 'More <i class="fa-solid fa-caret-down"></i>';
        var menu = document.createElement('div');
        menu.id = MENU_ID;
        menu.className = 'rf-dropdown-menu rf-dropdown-menu--sm rf-dropdown-menu-popout';   // popout: portals to body, escaping .header-nav's overflow: hidden
        // The popout sits under <body> while open, so its links can't inherit the
        // area bar's boost settings; without these htmx swaps the whole body.
        ['hx-boost', 'hx-target', 'hx-select', 'hx-swap', 'hx-push-url'].forEach(function (name) {
            if (navEl.hasAttribute(name)) menu.setAttribute(name, navEl.getAttribute(name));
        });
        overflow.appendChild(trigger);
        overflow.appendChild(menu);
        navEl.appendChild(overflow);
        return overflow;
    }

    function update() {
        var navEl = nav();
        if (!navEl) return;
        var links = navItems(navEl);
        var overflow = ensureOverflow(navEl);
        var trigger = overflow.querySelector('button');
        var menu = menuEl();
        if (!trigger || !menu) return;

        links.forEach(function (item) { item.classList.remove('header-navlink--collapsed'); });
        overflow.hidden = true;
        var room = navEl.clientWidth;
        var gap = parseFloat(getComputedStyle(navEl).gap) || 0;
        var used = 0, firstHidden = links.length;
        for (var i = 0; i < links.length; i++) {
            used += links[i].offsetWidth + (i ? gap : 0);
            if (used > room) { firstHidden = i; break; }
        }
        if (firstHidden === links.length) return;
        overflow.hidden = false;
        var triggerWidth = overflow.offsetWidth + gap;
        while (firstHidden > 0 && used + triggerWidth > room) {
            firstHidden--;
            used -= links[firstHidden].offsetWidth + (firstHidden ? gap : 0);
        }
        menu.innerHTML = '';
        var hasActive = false;
        links.forEach(function (a, i) {
            if (i < firstHidden) return;
            a.classList.add('header-navlink--collapsed');
            var link = navItemLink(a);
            if (!link) return;
            if (link.getAttribute('aria-current')) hasActive = true;
            if (link.tagName === 'BUTTON') {
                var sourceMenu = document.getElementById(link.getAttribute('aria-controls'));
                var heading = document.createElement('div');
                heading.className = 'rf-dropdown-title';
                heading.textContent = link.textContent.trim();
                menu.appendChild(heading);
                sourceMenu.querySelectorAll('.rf-dropdown-item, .rf-dropdown-divider').forEach(function (entry) {
                    menu.appendChild(entry.cloneNode(true));
                });
                return;
            }
            var item = document.createElement('a');
            item.className = 'rf-dropdown-item' + (link.getAttribute('aria-current') ? ' active' : '');
            item.href = link.href;
            item.textContent = link.textContent;
            menu.appendChild(item);
        });
        if (hasActive) trigger.setAttribute('aria-current', 'page');
        else trigger.removeAttribute('aria-current');
        if (window.htmx) htmx.process(menu);   // boost the new links (settings copied onto the menu above)
    }

    // Hub tab strip scrolls sideways on narrow screens; keep the active tab in view
    function revealActiveTab() {
        var strip = document.querySelector('#shell-subnav .tabs');
        var active = strip && strip.querySelector('.tabs-button--active');
        if (!active || strip.scrollWidth <= strip.clientWidth) return;
        var left = active.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
        strip.scrollLeft = left - (strip.clientWidth - active.offsetWidth) / 2;
    }

    function selectModule(sidebar, key) {
        sidebar.querySelectorAll('[data-shell-module]').forEach(function (link) {
            var selected = link.getAttribute('data-shell-module') === key;
            link.classList.toggle('nav__link--active', selected);
            if (selected) link.setAttribute('aria-current', 'true');
            else link.removeAttribute('aria-current');
        });
    }

    function restoreModuleGroups() {
        if (window.sidenav && window.sidenav.restoreGroups) window.sidenav.restoreGroups();
    }

    function resetMobileModule(sidebar) {
        if (!sidebar || !sidebar._browsingModule) return;
        if (moduleRequest) {
            moduleRequest.xhr.abort();
            moduleRequest = null;
        }
        var target = sidebar.querySelector('#shell-sidebar-module-links');
        target.replaceChildren(sidebar._originalModule.cloneNode(true));
        target.removeAttribute('aria-busy');
        htmx.process(target);
        selectModule(sidebar, sidebar.getAttribute('data-shell-current-module'));
        restoreModuleGroups();
        sidebar._browsingModule = false;
    }

    function scheduleUpdate() {
        clearTimeout(timer);
        timer = setTimeout(function () {
            update();
            revealActiveTab();
            if (!window.matchMedia('(max-width: 599px)').matches) {
                resetMobileModule(document.getElementById('shell-sidebar'));
            }
        }, 60);
    }

    document.addEventListener('DOMContentLoaded', function () {
        update();
        revealActiveTab();
    });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleUpdate);
    window.addEventListener('resize', scheduleUpdate);

    // A module request replaces only the drawer's lower links.  Remember that
    // temporary browsing choice, then restore the actual page's module before
    // the drawer is opened again or when its desktop sidebar comes back.
    document.addEventListener('click', function (e) {
        var moduleLink = e.target.closest('[data-shell-module]');
        if (moduleLink && window.matchMedia('(max-width: 599px)').matches && window.htmx) {
            if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            htmx.trigger(moduleLink, 'shell:module');
            return;
        }
        var toggle = e.target.closest('[data-sidenav-toggle]');
        var sidebar = document.getElementById('shell-sidebar');
        if (toggle && sidebar && !sidebar.classList.contains('sidenav--mobile-open')) {
            resetMobileModule(sidebar);
        }
    }, true);

    document.body.addEventListener('htmx:beforeRequest', function (e) {
        var elt = e.detail.elt;
        if (!elt || !elt.hasAttribute('data-shell-module')) return;
        var sidebar = elt.closest('#shell-sidebar');
        var target = e.detail.target;
        if (!sidebar._originalModule) {
            sidebar._originalModule = document.createDocumentFragment();
            Array.from(target.childNodes).forEach(function (node) {
                sidebar._originalModule.appendChild(node.cloneNode(true));
            });
        }
        sidebar._browsingModule = true;
        var previous = document.createDocumentFragment();
        while (target.firstChild) previous.appendChild(target.firstChild);
        moduleRequest = {xhr: e.detail.xhr, previous: previous};
        target.appendChild(sidebar.querySelector('#shell-sidebar-loading').content.cloneNode(true));
        target.setAttribute('aria-busy', 'true');
    });

    document.body.addEventListener('htmx:afterSwap', function (e) {
        var target = e.detail && e.detail.target;
        if (!target || target.id !== 'shell-sidebar-module-links') return;
        var sidebar = target.closest('#shell-sidebar');
        selectModule(sidebar, e.detail.requestConfig.elt.getAttribute('data-shell-module'));
        restoreModuleGroups();
    });

    document.body.addEventListener('htmx:afterRequest', function (e) {
        if (!moduleRequest || moduleRequest.xhr !== e.detail.xhr) return;
        var target = e.detail.target;
        if (!e.detail.successful) {
            target.replaceChildren(moduleRequest.previous);
            htmx.process(target);
        }
        target.removeAttribute('aria-busy');
        moduleRequest = null;
    });

    // ── Record rail and panel ────────────────────────────────────────────
    var PIN_KEY = 'shell-panel-pinned';
    var RECORD_CHROME = ['shell-panel', 'shell-rail'];
    var narrow = window.matchMedia('(max-width: 1199px)');   // below lg the panel floats whatever the pin

    function shellEl() { return document.querySelector('.shell'); }
    function panelEl() { return document.getElementById('shell-panel'); }

    function pinPreferred() {
        try { return localStorage.getItem(PIN_KEY) !== '0'; } catch (e) { return true; }
    }

    function isPinned() {
        var shell = shellEl();
        return !!shell && shell.classList.contains('shell--pinned') && !narrow.matches;
    }

    // ── #rail-<section> in the URL ──
    function hashSection() {
        var m = window.location.hash.match(/^#rail-(.+)$/);
        return m ? m[1] : null;
    }

    // replaceState keeps the entry's state (htmx and sheets.js mark their own).
    function writeHash(name) {
        var url = window.location.pathname + window.location.search;
        history.replaceState(history.state, '', name ? url + '#rail-' + name : url);
    }

    // The hash is typed input: only a section this page renders, with its rail button, opens.
    function hasSection(name) {
        var panel = panelEl();
        var key = CSS.escape(name);
        return !!panel && !!document.querySelector('#shell-rail [data-rail="' + key + '"]')
            && !!panel.querySelector('[data-panel-section="' + key + '"]');
    }

    function setRailActive(name) {
        document.querySelectorAll('#shell-rail [data-rail]').forEach(function (btn) {
            btn.classList.toggle('shell__rail-btn--active', btn.getAttribute('data-rail') === name);
        });
    }

    function openPanel(name) {
        var panel = panelEl();
        var btn = document.querySelector('#shell-rail [data-rail="' + name + '"]');
        if (!panel || !btn) return;
        panel.querySelectorAll('[data-panel-section]').forEach(function (el) {
            el.hidden = el.getAttribute('data-panel-section') !== name;
        });
        panel.querySelectorAll('[data-panel-footer]').forEach(function (el) {
            el.hidden = el.getAttribute('data-panel-footer') !== name;
        });
        panel.querySelector('[data-panel-title]').textContent = btn.getAttribute('title');
        setRailActive(name);
        shellEl().classList.toggle('shell--pinned', pinPreferred());
        panel.hidden = false;
        panel.setAttribute('data-panel-current', name);
        syncPinButton();
        // clamp.js measures "Show more" toggles once on load; a section that was
        // hidden then measured 0 high, so measure again now that it shows.
        if (window.clampInitToggles) window.clampInitToggles();
        // Anything that only starts up while visible (acqs-property-map.js) hears
        // which section just showed.
        var section = panel.querySelector('[data-panel-section="' + name + '"]');
        if (section) section.dispatchEvent(new CustomEvent('shell:panel-open', { bubbles: true }));
    }

    function closePanel() {
        var panel = panelEl();
        if (!panel) return;
        panel.hidden = true;
        panel.removeAttribute('data-panel-current');
        setRailActive(null);
        // The pinned grid reserves the panel column, so a closed panel drops
        // the class; openPanel puts it back from the saved preference.
        shellEl().classList.remove('shell--pinned');
    }

    function syncPinButton() {
        var btn = document.querySelector('#shell-panel [data-panel-pin]');
        if (!btn) return;
        var pinned = isPinned();
        btn.title = pinned ? 'Unpin (float over the page)' : 'Pin as a column';
        btn.querySelector('i').className = pinned ? 'fa-solid fa-thumbtack' : 'fa-solid fa-table-columns';
    }

    // A close the user makes drops the section from the URL.
    function closePanelAndHash() {
        closePanel();
        if (hashSection()) writeHash(null);
    }

    // The page's state on arrival: a #rail-<section> hash opens that section
    // (pinned or floating); otherwise pinned pages open the default section
    // and everything else starts with the panel closed. The pinned class is on
    // only while the panel is open, so the grid never has an empty column.
    // fromUrl is false on an htmx page swap: the hash then still belongs to
    // the page being left (see htmx:beforeHistoryUpdate below).
    function initPanel(fromUrl) {
        var shell = shellEl();
        var panel = panelEl();
        if (!shell) return;
        shell.classList.toggle('shell--pinned', !!panel && pinPreferred());
        var linked = fromUrl ? hashSection() : null;
        if (linked && !hasSection(linked)) {
            writeHash(null);
            linked = null;
        }
        if (!panel) return;
        if (linked) openPanel(linked);
        else if (isPinned()) openPanel(panel.getAttribute('data-panel-default'));
        else closePanel();
        syncPinButton();
    }

    document.addEventListener('click', function (e) {
        var railBtn = e.target.closest('#shell-rail [data-rail]');
        if (railBtn) {
            var name = railBtn.getAttribute('data-rail');
            var panel = panelEl();
            if (panel && !panel.hidden && panel.getAttribute('data-panel-current') === name) {
                closePanelAndHash();
            } else {
                openPanel(name);
                writeHash(name);
            }
            return;
        }
        if (e.target.closest('#shell-panel [data-panel-close]')) {
            closePanelAndHash();
            return;
        }
        if (e.target.closest('#shell-panel [data-panel-pin]')) {
            var shell = shellEl();
            var pinned = !shell.classList.contains('shell--pinned');
            shell.classList.toggle('shell--pinned', pinned);
            try { localStorage.setItem(PIN_KEY, pinned ? '1' : '0'); } catch (err) { /* private mode */ }
            syncPinButton();
            return;
        }
        // A floating panel closes on a click outside it. There is no backdrop:
        // modals.js adopts the first .backdrop on the page as its own.
        var open = panelEl();
        if (open && !open.hidden && !isPinned() && !e.target.closest('#shell-panel, .sheet, .modal, .dropdown-menu, .rf-dropdown-menu')) {
            closePanelAndHash();
        }
        // A tab that is a plain link (the rental page's) reloads the page; carry
        // the open section to it. htmx pane tabs (every acqs record page) have already
        // prevented the default by the time this runs, and keep the hash at
        // beforeHistoryUpdate below.
        var tab = e.target.closest('#page-content .tabs a.tabs-button[href]');
        if (tab && hashSection() && !e.defaultPrevented && e.button === 0 && !tab.target
                && !(e.ctrlKey || e.metaKey || e.shiftKey || e.altKey)) {
            e.preventDefault();
            window.location.href = tab.href.split('#')[0] + window.location.hash;
        }
    });

    // htmx is about to write the URL. Within the page (a tab pane, a filter) the
    // open section stays in it. A page swap has already replaced the rail and
    // panel (htmx:beforeSwap runs first) and starts from the new page's default.
    document.body.addEventListener('htmx:beforeHistoryUpdate', function (e) {
        var update = e.detail.history;
        var name = hashSection();
        if (!name || update.path.indexOf('#') !== -1) return;
        if (e.detail.target && e.detail.target.id === 'page-content') return;
        update.path += '#rail-' + name;
    });

    narrow.addEventListener('change', syncPinButton);

    // The bundle loads at the end of <body>, after the rail markup, so this
    // runs before first paint on a full page load.
    initPanel(true);

    // ── Chrome follows the page ──────────────────────────────────────────
    function syncRecordChrome(doc) {
        var shell = shellEl();
        if (!shell) return;
        RECORD_CHROME.forEach(function (id) {
            var fresh = doc.getElementById(id);
            var current = document.getElementById(id);
            if (fresh && current) current.replaceWith(fresh);
            else if (fresh) shell.appendChild(fresh);
            else if (current) current.remove();
            if (fresh && window.htmx) htmx.process(fresh);
        });
        initPanel(false);
    }

    function replaceFrom(doc, id) {
        var fresh = doc.getElementById(id);
        var current = document.getElementById(id);
        if (!fresh || !current) return null;
        current.replaceWith(fresh);
        if (window.htmx) htmx.process(fresh);
        return fresh;
    }

    document.body.addEventListener('htmx:beforeSwap', function (e) {
        var target = e.detail && e.detail.target;
        if (!target || target.id !== 'page-content' || !e.detail.shouldSwap) return;
        var xhr = e.detail.xhr;
        if (!xhr || !xhr.responseText) return;
        var doc = new DOMParser().parseFromString(xhr.responseText, 'text/html');
        if (!doc.getElementById('shell-sidebar')) return;   // not a shell page (login, other layout)
        if (moduleRequest) {
            moduleRequest.xhr.abort();
            moduleRequest = null;
        }
        var sidebar = replaceFrom(doc, 'shell-sidebar');
        var areaNav = document.getElementById('shell-areas');
        if (areaNav) closeNavPopouts(areaNav);
        replaceFrom(doc, 'shell-areas');
        replaceFrom(doc, 'shell-subnav');   // hub tabs come and go with the page
        syncRecordChrome(doc);              // so do a record page's rail and panel
        if (sidebar && window.sidenav) {
            if (window.sidenav.restoreGroups) window.sidenav.restoreGroups();
            if (window.sidenav.mobileClose) window.sidenav.mobileClose();   // the drawer went with the old sidebar; drop its veil
        }
        scheduleUpdate();
    });
})();
